import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ActivityHistoryCard } from '@europrocurement/flexy-components';
import { UtilisateurTaches } from '@europrocurement/l2d-domain';
import { useTheme } from '@mui/material';
import { addNoteIcon, addTaskIcon, preview, forbiddenIcon } from '@europrocurement/l2d-icons';
import { UseKeycloakService } from '@europrocurement/l2d-keycloak';
import { PrescripteurTachesApiObject } from '@europrocurement/l2d-domain/reducers/tiers/slices/prescripteurTachesSlice';
import { DossierTachesApiObject } from '@europrocurement/l2d-domain/reducers/formalites/slices/dossierTachesSlice';
import useTasksHook from '../../../../Tasks/hook/useTasksHook';
import TasksCardContent from '../../../../Tasks/components/TasksCardContent/TasksCardContent';
import ActivitiesCardContent from '../ActivitiesCardContent/ActivitiesCardContent';
import useActivitiesHook from '../../../hooks/useActivitiesHook';
import { useCurrentDossierCtx } from '../../../providers/CurrentDossierProvider';

interface ActivityTabProps {
    showTask?: boolean;
    showTechniqueLogs?: boolean;
    noteMemo?: boolean;
    showNotes?: boolean;
    showLogData?: boolean;
    requestLogActionLimited?: boolean;
}

/**
 * Onglet d'activités pour un dossier
 * Affiche les notes et actions liées au dossier sélectionné
 */
const ActivityTab: React.FunctionComponent<ActivityTabProps> = function ({
    showTask = true,
    showNotes = true,
    showTechniqueLogs = false,
    noteMemo = true,
    showLogData = true,
    requestLogActionLimited = true,
}) {
    const { palette } = useTheme();
    const { dossier } = useCurrentDossierCtx();
    const [showLogsTechniques, setShowLogsTechniques] = useState(false);

    const {
        activities,
        activitiesLoading,
        handleAddNote,
        handleEditNote,
        handleDeleteNote,
        loadActivitiesTechniques,
    } = useActivitiesHook({
        useMemoClient: noteMemo,
        requestLogActionLimited,
        loadNotes: true,
        loadLogs: true,
    });

    const keycloakService = UseKeycloakService();
    const userIdentifier = keycloakService.getTokenParsed()?.preferred_username;
    const {
        orderedTasks,
        tasksLoading,
        handleAddTask,
        handleCheckTask,
        handleEditTask,
        handleDeleteTask,
    } = useTasksHook({
        apiTarget: 'formalite',
        linkedEntityId: dossier ? (dossier.id?.toString() as string) : undefined,
    });

    const handleLoadTechniques = useCallback(() => {
        setShowLogsTechniques((prev) => !prev);
    }, []);

    const textLogsTechnique = showLogsTechniques
        ? 'Masquer les actions techniques'
        : 'Afficher les actions techniques';

    const showIconTechniques = showLogsTechniques ? forbiddenIcon : preview;

    useEffect(() => {
        loadActivitiesTechniques(showLogsTechniques);
    }, [showLogsTechniques, loadActivitiesTechniques]);

    const actions = useMemo(
        () =>
            [
                showNotes && {
                    id: 'add-note',
                    label: 'Ajouter une note',
                    shortLabel: 'Ajouter',
                    icon: addNoteIcon,
                    onClick: handleAddNote,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
                showTask && {
                    id: 'add-task',
                    label: 'Ajouter une tâche',
                    shortLabel: 'Ajouter',
                    icon: addTaskIcon,
                    onClick: handleAddTask,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
                showTechniqueLogs && {
                    id: 'show-technique',
                    label: textLogsTechnique,
                    shortLabel: 'Afficher',
                    icon: showIconTechniques,
                    onClick: handleLoadTechniques,
                    variant: 'text' as const,
                    color: palette.grey[700],
                },
            ].filter((action): action is Exclude<typeof action, false> => action !== false),
        [
            handleAddNote,
            handleAddTask,
            handleLoadTechniques,
            palette.grey,
            showIconTechniques,
            textLogsTechnique,
            showTechniqueLogs,
            showTask,
            showNotes,
        ],
    );

    return (
        <ActivityHistoryCard actions={actions}>
            {showTask && dossier && (
                <TasksCardContent
                    tasks={
                        orderedTasks as
                            | DossierTachesApiObject[]
                            | PrescripteurTachesApiObject[]
                            | UtilisateurTaches[]
                    }
                    tasksLoading={tasksLoading}
                    onCheckTask={handleCheckTask}
                    onEditTask={handleEditTask}
                    onDeleteTask={handleDeleteTask}
                    userIdentifier={userIdentifier}
                />
            )}

            <ActivitiesCardContent
                activities={activities}
                onEditNote={handleEditNote}
                onDeleteNote={handleDeleteNote}
                activitiesLoading={activitiesLoading}
                showLogData={showLogData}
                showNotes={showNotes}
            />
        </ActivityHistoryCard>
    );
};

export default ActivityTab;
